import React from 'react';
import { logger } from '@/utils/logger';
import { supabase } from '@/lib/supabase';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { RichTextEditor, RichTextContent } from '@/components/RichTextEditor';
import { useTranslation } from 'react-i18next';

interface Instruction {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export function Instructions() {
  const { t } = useTranslation();
  const [instructions, setInstructions] = React.useState<Instruction[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [editingInstruction, setEditingInstruction] = React.useState<Instruction | null>(null);
  
  // Use separate state variables for title and content
  const [title, setTitle] = React.useState('');
  const [content, setContent] = React.useState('');
  
  // Add the ref inside the component
  const titleInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    fetchInstructions();
  }, []);

  async function fetchInstructions() {
    try {
      const { data, error } = await supabase
        .from('producer_instructions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInstructions(data || []);
    } catch (err) {
      logger.error('Error fetching instructions:', err);
      setError('Failed to load instructions');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(instruction: Instruction) {
    logger.debug('Editing instruction:', instruction);
    
    // Set the individual state variables
    setTitle(String(instruction.title || ''));
    setContent(String(instruction.content || ''));
    
    // Then set the editing state
    setEditingInstruction(instruction);
    
    // Scroll to the form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    try {
      if (editingInstruction) {
        logger.debug('Updating with title:', title, 'and content:', content);
        
        const { error } = await supabase
          .from('producer_instructions')
          .update({
            title: title,
            content: content
          })
          .eq('id', editingInstruction.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('producer_instructions')
          .insert({
            title: title,
            content: content
          });

        if (error) throw error;
      }

      // Reset form and refetch data
      setTitle('');
      setContent('');
      setEditingInstruction(null);
      fetchInstructions();
    } catch (err) {
      logger.error('Error saving instruction:', err);
      setError('Failed to save instruction');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm(t('admin.instructions.confirmDelete'))) {
      return;
    }

    try {
      const { error } = await supabase
        .from('producer_instructions')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchInstructions();
    } catch (err) {
      logger.error('Error deleting instruction:', err);
      setError('Failed to delete instruction');
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-4 shadow rounded-lg">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="mt-4 h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="instructions-container" className="instructions-container">
      <h1 className="text-2xl font-semibold text-gray-900">{t('admin.instructions.title', 'Instruktioner')}</h1>

      <div className="mt-8 max-w-3xl">
        {editingInstruction && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-blue-700 font-medium">{t('admin.instructions.editing')}: {editingInstruction.title}</p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4" role="form">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              {t('admin.instructions.titleLabel')} {editingInstruction && <span className="text-blue-600">({t('admin.instructions.editing')})</span>}
            </label>
            <input
              type="text"
              id="title"
              ref={titleInputRef}
              required
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
              className={`mt-1 block w-full rounded-md shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${
                editingInstruction ? 'border-blue-300 bg-blue-50' : 'border-gray-300'
              }`}
              style={{ padding: '0.75rem' }}
            />
          </div>

          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700">
              {t('admin.instructions.contentLabel', 'Content')}
            </label>
            <RichTextEditor
              value={content}
              onChange={(value) => setContent(value)}
              placeholder={t('admin.instructions.contentPlaceholder', 'Instructions content...')}
              className="mt-1"
              height={200}
            />
          </div>

          <div>
            <button
              type="submit"
              data-testid={editingInstruction ? "save-instruction" : "add-instruction"}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              {editingInstruction ? (
                t('admin.instructions.updateButton', 'Update Instruction')
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('admin.instructions.addButton', 'Add Instruction')}
                </>
              )}
            </button>
            {editingInstruction && (
              <button
                type="button"
                onClick={() => {
                  setEditingInstruction(null);
                  setTitle('');
                  setContent('');
                }}
                className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                {t('admin.common.cancel', 'Cancel')}
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="mt-8">
        <div className="space-y-4">
          {instructions.length === 0 ? (
            <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
              {t('admin.instructions.noInstructions', 'No instructions available. Create one using the form above.')}
            </div>
          ) : (
            instructions.map((instruction) => (
              <div
                key={instruction.id}
                data-testid="instruction-item"
                className="bg-white shadow rounded-lg overflow-hidden instruction-item"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        {instruction.title}
                      </h3>
                      <div className="mt-2 prose prose-sm max-w-none text-gray-500 instruction-detail" data-testid="instruction-detail">
                        <RichTextContent content={instruction.content} />
                      </div>
                      <div className="mt-2 text-sm text-gray-400">
                        {t('admin.instructions.lastUpdated')}: {new Date(instruction.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0 flex space-x-4">
                      <button
                        onClick={() => handleEdit(instruction)}
                        data-testid="edit-instruction"
                        aria-label="Edit instruction"
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(instruction.id)}
                        data-testid="delete-instruction"
                        aria-label="Delete instruction"
                        className="text-red-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}